import React from 'react';
import { Redirect, Link } from 'react-router-dom';
import gql from 'graphql-tag';
import { Query } from 'react-apollo';
import { registerClass, currySC, IFNTheme, cns } from '@food/css-manager';
import { IPageData } from '../DefaultPageData';
import { canView } from '../../utils/canView';
import { isLogged } from '@food/auth/build/dist/logic/auth';
import { generateMagazineDetailUrl } from '../../utils/urls';
import { Spinner } from '../Spinner';
import { Magazine } from '../../server-types';
import { MagazineCollectionHistory } from '../MagazineCollectionHistory';
import { MagazineSlideshow } from '../MagazineSlideshow';
import { magazineLastIssuesSliderQuery } from '../../utils/fragments';
import { useTranslation } from 'react-i18next';
//import { hasSubscription } from '../../utils/hasSubscription';
//import { SubscriptionBannerStatusAccess } from '../SubscriptionBannerStatusAccess';
import { SubscriptionBannerStatusActive } from '../SubscriptionBannerStatusActive';
import { Icon } from '@food/ui';
import * as Sentry from '@sentry/browser';
import { DefaultLayout } from '../../utils/layout';
import { useUserMagazineData } from '../../utils/hooks';

export const magazineReadQuery = gql`
	query MagazineReadquery($magazineId: ID!) {
		magazine: node(id: $magazineId) {
			... on Magazine {
				id
				name
				fullUrl
				previewUrl
				customVisibilityType
				customVisibilityArgs
				MagazineCollection {
					id
					name
					BrandSf
					startYear
					visibilityType
					visibilityArgs
				}
			}
		}
	}
`;

export const magazineStyleClass = registerClass(
	(t: IFNTheme) => `

	.${sc('section')},
	.${sc('iframeContainer')} {
		padding: ${t.ratios.xl * t.font.baseSize}px 0 ${t.ratios.l * t.font.baseSize}px;
	}

	.${sc('iframeContainer')} {
		position: relative;
	}
	
	.${sc('popupLayer')} {
		width: 100%;
		height: 100%;
		background-color: rgba(0,0,0,0.6);
		display: flex;
		justify-content: center;
		align-items: center;
		position: absolute;
		top: 0;
		left: 0;
	}
	
	.${sc('popupContainer')} {
		width: ${15 * t.font.baseSize}px;  // AB valore numerico
		padding: ${t.ratios.l * 3 * t.font.baseSize}px ${t.ratios.l * t.font.baseSize}px ${t
			.ratios.l * t.font.baseSize}px;
		background-color: ${t.colors.white};
		border-radius: ${t.radius * 2 * t.font.baseSize}px;
		position: relative;
	}

	.${sc('popupContent')} {
		text-align: center;
	}
	
	.${sc('popupClose')} {
		width: ${((t.ratios.l * 3) / 2) * t.font.baseSize}px;
		height: ${((t.ratios.l * 3) / 2) * t.font.baseSize}px;
		background-color: ${t.colors.grey.light};
		border-radius: 50%;
		display: flex;
		justify-content: center;
		align-items: center;
		position: absolute;
		top: ${t.ratios.s * t.font.baseSize}px;
		right: ${t.ratios.s * t.font.baseSize}px;
		color: ${t.colors.grey.dark};
		transition: color .3s, background-color .3s;
		cursor: pointer;
		
		&:hover {
			background-color: ${t.colors.grey.dark};
			color: ${t.colors.grey.light};
		}
	}
	
	.${sc('iframe')} {
		width: 100%;
		height: 50vh;
		min-height: 500px;
	}

	&.${sc('layout', DefaultLayout.TABLET)}, &.${sc('layout', DefaultLayout.MOBILE)} {
		.${sc('iframe')} {
			min-height: 115vw;
		}
	}

	.${sc('back')} {
		font-size: ${t.ratios.l}em;
		font-weight: ${t.font.weights.bold};

		.${sc('icon')} {
			vertical-align: middle;
		}
	}
  `,
);

const sc = currySC(magazineStyleClass);

export const MagazineRead: React.FunctionComponent<IPageData & {
	magazineId: string;
}> = ({ location, magazineId, layout }) => {
	const { t } = useTranslation();
	const { loading: loading1, userMagazineData } = useUserMagazineData();
	// const [isPopupOpen, setPopupState] = useState(false);
	// const [alreadyOpen, setFirstOpen] = useState(false);

	/*useEffect(() => {
		let timeoutId: NodeJS.Timeout;
		if (!isPopupOpen && !alreadyOpen) {
			timeoutId = setTimeout(() => {
				setPopupState(true);
				setFirstOpen(true);
			}, 25000);
		}
		return () => clearTimeout(timeoutId);
	}, [isPopupOpen, alreadyOpen]);*/

	const page = location.search.page || 1;

	return (
		<Query<{ magazine: Magazine }, { magazineId: string }>
			query={magazineReadQuery}
			variables={{ magazineId }}
		>
			{({ error, loading, data }) => {
				if (error) {
					// TODO
					console.error(error);
					return <span>{error}</span>;
				}

				if (loading1 || loading || !data) {
					return <Spinner />;
				}

				const { magazine } = data;

				const {
					fullUrl,
					MagazineCollection: collection,
					// previewUrl,
					issueNumber,
					name,
				} = magazine;

				const visibilityArgs = {
					visibility: magazine.customVisibilityType || collection.visibilityType,
					args: magazine.customVisibilityType
						? magazine.customVisibilityArgs
						: collection.visibilityArgs,
					user: userMagazineData,
					isLogged: isLogged(),
				};

				const hasVisibility = canView(visibilityArgs);
				//const isSubscribed = hasSubscription(visibilityArgs);
				let finalUrl = fullUrl;

				if (!hasVisibility) {
					// non ho visibilita', ritorno alla pagina di controllo
					return <Redirect to={generateMagazineDetailUrl(magazine.id)} />;
				}

				if (hasVisibility && !finalUrl) {
					// in questo caso segnalo il problema a sentry!
					Sentry.captureException(new Error('Magazine senza url calameo'));
				}

				if (page) {
					let pre = '?';
					if (finalUrl.indexOf('?') !== -1) {
						pre = '&';
					}
					finalUrl += `${pre}page=${page}`;
				}

				let pre = '?';
				if (finalUrl.indexOf('?') !== -1) {
					pre = '&';
				}
				finalUrl += `${pre}utm_source=kc`;

				const issue = (issueNumber ? `N.${issueNumber} ` : '') + name;
				document.title = "Leggi: " + issue + " - " + t(collection.BrandSf) + " - " + collection.name + " - Knowledge Center";

				return (
					<div className={cns(magazineStyleClass, sc('layout', layout))}>
						{/*hasVisibility && !isSubscribed && (
							<SubscriptionBannerStatusAccess visibilityArgs={visibilityArgs} />
						)*/}
						<SubscriptionBannerStatusActive user={userMagazineData} />

						<Link
							className={sc(`back`)}
							to={generateMagazineDetailUrl(magazine.id)}
						>
							<Icon name={'chevron_left'} className={sc('icon')} />
							{t(`magazine_back_to_summary`)}
						</Link>

						<div className={sc('iframeContainer')}>
							{/*{isPopupOpen && (
											<div className={sc('popupLayer')}>
												<div className={sc('popupContainer')}>
													<div className={sc('popupContent')}>ADV</div>
													<div
														className={sc('popupClose')}
														onClick={() => {
															setPopupState(false);
														}}
													>
														X
													</div>
												</div>
											</div>
										)}*/}
							<iframe
								src={finalUrl}
								className={sc('iframe')}
								title={'read-iframe'}
								allowFullScreen={true}
							/>
						</div>

						<MagazineSlideshow
							className={sc('section')}
							layout={layout}
							query={magazineLastIssuesSliderQuery}
							variables={{}}
							sectionTitle={t`last_issues`}
							excluded={[magazine.id]}
						/>
						<MagazineCollectionHistory
							className={sc('section')}
							collectionId={collection.id}
							layout={layout}
						/>
					</div>
				);
			}}
		</Query>
	);
};
